/**
 * ShadowModeContext.js
 * 
 * Context for managing Platform Owner "View As" shadow sessions.
 * Handles shadow session state, token management, and exit functionality.
 * 
 * P0 FIX: Shadow tokens are STRICTLY scoped to active shadow sessions only.
 * On init, if shadow_original_token exists but session is not valid, it's cleared.
 */
import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import { toast } from 'sonner';

const ShadowModeContext = createContext(null);

export const useShadowMode = () => {
  const context = useContext(ShadowModeContext);
  if (!context) {
    throw new Error('useShadowMode must be used within a ShadowModeProvider');
  }
  return context;
};

export const ShadowModeProvider = ({ children }) => {
  const [isInShadowMode, setIsInShadowMode] = useState(false);
  const [shadowSession, setShadowSession] = useState(null);
  const [loading, setLoading] = useState(false);
  const [originalToken, setOriginalToken] = useState(null);
  const initRef = useRef(false); // Prevent double init

  const API_URL = process.env.REACT_APP_BACKEND_URL || window.location.origin;

  // Global interceptor: surface shadow-mode 403 as a toast
  useEffect(() => {
    const id = axios.interceptors.response.use(
      (res) => res,
      (error) => {
        if (
          error.response &&
          error.response.status === 403 &&
          error.response.data?.error_code === 'SHADOW_SESSION_READ_ONLY'
        ) {
          toast.error('Shadow mode is read-only', {
            description: 'You cannot make changes while viewing as another user.',
            duration: 4000,
          });
        }
        return Promise.reject(error);
      }
    );
    return () => axios.interceptors.response.eject(id);
  }, []);

  // P0 FIX: Clear stale shadow tokens - this should NEVER restore tokens
  const clearStaleShadowTokens = useCallback(() => {
    sessionStorage.removeItem('shadow_original_token');
    setOriginalToken(null);
  }, []);

  // Check if current token is a shadow session token
  const checkShadowMode = useCallback(async () => {
    // Read via the per-tab token resolver — in a shadow tab this returns the
    // sessionStorage shadow token; in the PO tab it returns localStorage token.
    var token = null;
    try {
      if (window.sessionStorage.getItem('is_shadow_tab') === '1') {
        token = window.sessionStorage.getItem('token');
      }
    } catch (_) { /* ignore */ }
    if (!token) {
      token = localStorage.getItem('token');
    }
    if (!token) {
      setIsInShadowMode(false);
      setShadowSession(null);
      clearStaleShadowTokens(); // P0 FIX: Always clear stale shadow tokens when no main token
      return;
    }

    try {
      const response = await axios.get(`${API_URL}/api/platform-owner/shadow-session/current`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (response.data.is_shadow_session) {
        setIsInShadowMode(true);
        setShadowSession({
          session_id: response.data.shadow_session_id,
          admin_user_id: response.data.admin_user_id,
          admin_email: response.data.admin_email,
          target_user_id: response.data.target_user_id,
          target_user_email: response.data.target_user_email,
          target_user_name: response.data.target_user_name,
          target_is_host: response.data.target_is_host,
          reason: response.data.shadow_reason,
          started_at: response.data.started_at,
          expires_at: response.data.expires_at
        });
      } else {
        setIsInShadowMode(false);
        setShadowSession(null);
        // P0 FIX: If NOT in shadow mode, clear any stale shadow_original_token
        clearStaleShadowTokens();
      }
    } catch (err) {
      console.error('[Shadow] Failed to check shadow mode:', err);
      setIsInShadowMode(false);
      setShadowSession(null);
      // P0 FIX: On error, clear stale shadow tokens
      clearStaleShadowTokens();
    }
  }, [API_URL, clearStaleShadowTokens]);

  // P0 FIX: On mount, validate and clean up shadow state
  useEffect(() => {
    if (initRef.current) return;
    initRef.current = true;
    
    const initShadow = async () => {
      
      // Check for orphaned shadow_original_token
      const shadowToken = sessionStorage.getItem('shadow_original_token');
      const currentToken = localStorage.getItem('token');
      
      if (shadowToken && !currentToken) {
        // Orphaned shadow token - clear it
        clearStaleShadowTokens();
        return;
      }
      
      // Validate current shadow session
      await checkShadowMode();
    };
    
    initShadow();
  }, [checkShadowMode, clearStaleShadowTokens]);
  
  // Listen for storage changes (in case token changes in another tab)
  useEffect(() => {
    const handleStorageChange = (e) => {
      if (e.key === 'token') {
        checkShadowMode();
      }
    };
    
    window.addEventListener('storage', handleStorageChange);
    return () => window.removeEventListener('storage', handleStorageChange);
  }, [checkShadowMode]);

  // Start a shadow session via the BACKEND ONE-TIME HANDOFF flow.
  //
  // ARCHITECTURE:
  //   1. Ask backend to mint a single-use, 60-second handoff record. The
  //      returned `enter_url` is a path-only URL (`/shadow/enter?handoff=<id>`)
  //      — no JWT in the URL, ever. Even if a user copy-pastes the URL into
  //      another browser (Safari etc.), the handoff is either expired or
  //      already redeemed and the second attempt fails closed.
  //   2. Open that URL in a new browser tab.
  //   3. The new tab calls `/shadow/redeem-handoff` (no auth header — the
  //      handoff IS the proof) and receives the actual shadow JWT, which is
  //      stored in that tab's sessionStorage only. The original PO tab's
  //      localStorage is never mutated.
  //
  // The previous in-tab token swap and URL-fragment handoff approaches were
  // both fragile in Microsoft Edge. The backend handoff is the canonical
  // implementation now.
  const startShadowSession = async (targetUserId, reason = 'support', notes = null) => {
    setLoading(true);
    try {
      const currentToken = localStorage.getItem('token');
      if (!currentToken) {
        throw new Error('No token available to start shadow session');
      }

      const response = await axios.post(
        `${API_URL}/api/platform-owner/shadow/create-handoff`,
        { target_user_id: targetUserId, reason, notes },
        { headers: { Authorization: `Bearer ${currentToken}` } }
      );

      const { enter_url, handoff_id } = response.data;
      if (!enter_url) {
        throw new Error('Server did not return a handoff entry URL');
      }

      const newTab = window.open(enter_url, '_blank', 'noopener=no');
      if (!newTab) {
        toast.error(
          'Pop-up blocked. Please allow pop-ups for this site, then try "View as User" again.'
        );
        return null;
      }

      toast.success('Opening shadow session in a new tab…');
      return { handoff_id };
    } catch (err) {
      console.error('[Shadow] Failed to start shadow session:', err);
      // Clean up on failure
      clearStaleShadowTokens();
      throw err;
    } finally {
      setLoading(false);
    }
  };

  // Exit shadow session.
  //
  // NEW-TAB STRATEGY: this function only runs inside the shadow tab. Because
  // the bootstrap in `public/index.html` patched Storage.prototype to redirect
  // 'token' reads/writes to sessionStorage in this tab, calling
  // `localStorage.setItem('token', admin_token)` here actually writes to
  // sessionStorage — so the original Platform Owner tab's localStorage is
  // never touched. We then attempt `window.close()`; if the browser blocks
  // it (some browsers refuse to close tabs they didn't open), we fall back
  // to a clean redirect away from the shadow surface.
  const exitShadowMode = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');

      const response = await axios.post(
        `${API_URL}/api/platform-owner/shadow-session/end`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );

      const { admin_token } = response.data;

      // In the shadow tab this clears the per-tab session token (Storage
      // proxy redirects to sessionStorage). The original PO tab's
      // localStorage `token` remains its own admin token — untouched.
      localStorage.setItem('token', admin_token || '');
      localStorage.setItem('token_stored_at', Date.now().toString());
      // Drop the per-tab marker so this tab is no longer treated as shadow.
      try { sessionStorage.removeItem('is_shadow_tab'); } catch (_) {}
      try { sessionStorage.removeItem('token'); } catch (_) {}
      try { sessionStorage.removeItem('token_stored_at'); } catch (_) {}
      sessionStorage.removeItem('shadow_original_token');

      // Clear shadow state
      setIsInShadowMode(false);
      setShadowSession(null);
      setOriginalToken(null);

      toast.success('Shadow session ended.');

      // Try to close the shadow tab. If the browser refuses (it usually
      // does for tabs not opened by this script), redirect to a neutral
      // login page so the operator never sees a half-resolved Platform
      // Owner shell that is not actually an admin in this tab.
      setTimeout(() => {
        try { window.close(); } catch (_) {}
        if (!window.closed) {
          // No usable token in this tab anymore — punt to /login. The
          // original PO tab still holds a valid admin session.
          window.location.replace('/login?_shadow_ended=' + Date.now());
        }
      }, 150);

      return true;
    } catch (err) {
      console.error('[Shadow] Failed to exit shadow mode:', err);
      
      // Fallback: in the new-tab flow we no longer keep a separate
      // "original admin token" copy in this tab — the shadow tab has only
      // the shadow token. If the /shadow-session/end call failed, just
      // tear down the per-tab token and punt to /login so the operator
      // can return to their (still-valid) Platform Owner tab.
      try { sessionStorage.removeItem('is_shadow_tab'); } catch (_) {}
      try { sessionStorage.removeItem('token'); } catch (_) {}
      try { sessionStorage.removeItem('token_stored_at'); } catch (_) {}
      sessionStorage.removeItem('shadow_original_token');
      try { window.close(); } catch (_) {}
      if (!window.closed) {
        window.location.replace('/login?_shadow_ended=' + Date.now());
      }

      throw err;
    } finally {
      setLoading(false);
    }
  };

  // Emergency exit — same teardown but without calling the backend.
  const emergencyExit = () => {
    try { sessionStorage.removeItem('is_shadow_tab'); } catch (_) {}
    try { sessionStorage.removeItem('token'); } catch (_) {}
    try { sessionStorage.removeItem('token_stored_at'); } catch (_) {}
    sessionStorage.removeItem('shadow_original_token');
    setIsInShadowMode(false);
    setShadowSession(null);
    setOriginalToken(null);
    try { window.close(); } catch (_) {}
    if (!window.closed) {
      window.location.replace('/login?_shadow_emergency=' + Date.now());
    }
  };

  const value = {
    isInShadowMode,
    shadowSession,
    loading,
    startShadowSession,
    exitShadowMode,
    emergencyExit,
    checkShadowMode,
    clearStaleShadowTokens // P0 FIX: Expose for AuthContext to call on login/logout
  };

  return (
    <ShadowModeContext.Provider value={value}>
      {children}
    </ShadowModeContext.Provider>
  );
};

export default ShadowModeContext;
